import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormQuestion, FormResponseAnswer, QuestionLink } from '../Utils/Types/FormBuilder';
import { compact, flatten } from 'lodash';

import useSpace from './useSpace';
import { LocaleType } from '../Utils/Types/Locales';
import appsignal from '../Services/ErrorReportingService';
import { moment } from '../I18n';

/**
 * This Hook is used to determine if a question should be visible or not
 * @param question - The question to check
 * @param control - The form control
 * @scope - The scope of the question
 */

interface useQuestionVisibilityProps {
  question: FormQuestion;
  questions: FormQuestion[];
  form: UseFormReturn<any>;
  scope: string;
}

export default function useQuestionVisibility({
  question,
  questions,
  scope,
  form,
}: useQuestionVisibilityProps) {
  const questionLinks = question.question_links || [];
  const { space } = useSpace();
  const answers = useWatch({ control: form.control, name: `${scope}answers_attributes` });

  if (questionLinks.length === 0) {
    return true;
  } else {
    // NOTE: if we have multiple question links
    // we check if any (some) of them match (OR condition)
    const visible = questionLinks.some((questionLink) => {
      return checkValue(questionLink, questions, answers, space.default_locale);
    });

    return visible;
  }
}

export function checkValue(
  questionLink: QuestionLink,
  questions: FormQuestion[],
  answers: FormResponseAnswer[] = [],
  defaultLocale: LocaleType,
) {
  const linkedQuestion = questions.find(
    (question) => question.uuid_ref === questionLink.target_ref,
  );
  const answer = answers.find((answer) => answer?.question_id == linkedQuestion?.id);

  if (!linkedQuestion || !answer) {
    return false;
  }

  const valueAsNumber = parseFloat(questionLink?.value || '0');
  const answerAsNumber = parseFloat(answer?.content || '0') || 0;

  const valueAsDate = moment(questionLink?.value).format('YYYY-MM-DD');
  const answerAsDate = moment(answer?.date).format('YYYY-MM-DD');

  const splittedTextValue = questionLink.value?.split('\n');

  const answerI18nContent = answer[`content_i18n_${defaultLocale}`];

  switch (linkedQuestion.field_type) {
    case 'attachment':
      switch (questionLink.operator) {
        case 'present':
          return !!answer?.attachments?.filter((attachment: any) => !attachment._destroy)?.length;
        case 'blank':
          return !answer?.attachments?.filter((attachment: any) => !attachment._destroy)?.length;
        default:
          appsignal.sendError(new Error(`Invalid operator: ${questionLink.operator}`));
          return false;
      }
    case 'checkbox':
    case 'radio':
      const selectedOptions = compact(flatten([answer?.question_option_ids])); // sometimes we have an array, sometime we have a single item
      const selectedOptionsRefs = selectedOptions.map((optionId: string) => {
        return linkedQuestion.question_options.find((option) => option.id == optionId)?.uuid_ref;
      });

      switch (questionLink.operator) {
        case 'eq':
          return questionLink.question_option_refs.some((optionRef: string) =>
            selectedOptionsRefs.includes(optionRef),
          );
        case 'not_eq':
          return !questionLink.question_option_refs.some((optionRef: string) =>
            selectedOptionsRefs.includes(optionRef),
          );
        case 'present':
          return selectedOptionsRefs.length > 0;
        case 'blank':
          return selectedOptionsRefs.length === 0;
        default:
          appsignal.sendError(new Error(`Invalid operator: ${questionLink.operator}`));
          return false;
      }
    case 'date':
      switch (questionLink.operator) {
        case 'eq':
          return answerAsDate === valueAsDate;
        case 'not_eq':
          return answerAsDate !== valueAsDate;
        case 'gt':
          return answerAsDate > valueAsDate;
        case 'lt':
          return answerAsDate < valueAsDate;
        case 'gte':
          return answerAsDate >= valueAsDate;
        case 'lte':
          return answerAsDate <= valueAsDate;
        case 'present':
          return !!answer?.date;
        case 'blank':
          return !answer?.date;
        default:
          appsignal.sendError(new Error(`Invalid operator: ${questionLink.operator}`));
          return false;
      }

    // location and phone number both use answer.content field
    case 'location':
    case 'phone_number':
      switch (questionLink.operator) {
        case 'eq':
          return splittedTextValue?.includes(answer?.content);
        case 'not_eq':
          return !splittedTextValue?.includes(answer?.content);
        case 'present':
          return !!answer?.content;
        case 'blank':
          return !answer?.content;
        case 'contains':
          return splittedTextValue?.some((value) => answer?.content?.includes(value));
        case 'not_contains':
          return !splittedTextValue?.some((value) => answer?.content?.includes(value));
        default:
          appsignal.sendError(new Error(`Invalid operator: ${questionLink.operator}`));
          return false;
      }
    case 'money':
      switch (questionLink.operator) {
        case 'eq':
          return answerAsNumber === valueAsNumber;
        case 'not_eq':
          return answerAsNumber !== valueAsNumber;
        case 'gt':
          return answerAsNumber > valueAsNumber;
        case 'lt':
          return answerAsNumber < valueAsNumber;
        case 'gte':
          return answerAsNumber >= valueAsNumber;
        case 'lte':
          return answerAsNumber <= valueAsNumber;
        case 'present':
          return !!answer?.content;
        case 'blank':
          return !answer?.content;
        default:
          appsignal.sendError(new Error(`Invalid operator: ${questionLink.operator}`));
          return false;
      }
    case 'picture':
      switch (questionLink.operator) {
        case 'present':
          return !!answer?.picture && !answer.picture_attributes?._destroy;
        case 'blank':
          return !answer?.picture || !!answer.picture_attributes?._destroy;
        default:
          appsignal.sendError(new Error(`Invalid operator: ${questionLink.operator}`));
          return false;
      }
    case 'text':
    case 'textarea':
      switch (questionLink.operator) {
        case 'eq':
          return splittedTextValue?.includes(answerI18nContent);
        case 'not_eq':
          return !splittedTextValue?.includes(answerI18nContent);
        case 'present':
          return !!answerI18nContent;
        case 'blank':
          return !answerI18nContent;
        case 'contains':
          return splittedTextValue?.some((value) => answerI18nContent?.includes(value));
        case 'not_contains':
          return !splittedTextValue?.some((value) => answerI18nContent?.includes(value));
        default:
          appsignal.sendError(new Error(`Invalid operator: ${questionLink.operator}`));
          return false;
      }
    default:
      appsignal.sendError(new Error(`Unsupported field type: ${linkedQuestion.field_type}`));
      return false;
  }
}
